import { uuidv4 } from '@/utils/uuid';

// Pont vers l'application tablette (Tablet Agent) qui embarque cette page dans une WebView : le navigateur ne peut
// ni ouvrir de socket TCP ni scanner le réseau, l'application le fait pour lui.
declare global {
  interface Window {
    ReactNativeWebView?: { postMessage: (message: string) => void };
  }
}

interface Pending {
  resolve: (payload: unknown) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

const pending = new Map<string, Pending>();
let listening = false;

export const isNative = () =>
  typeof window !== 'undefined' && Boolean(window.ReactNativeWebView);

const onMessage = (event: MessageEvent) => {
  let message: { id?: string; type?: string; payload?: unknown } | null;
  try {
    message = typeof event.data === 'string' ? JSON.parse(event.data) : null;
  } catch {
    return;
  }
  const call = message?.id ? pending.get(message.id) : undefined;
  if (!message || !message.id || !call) return;
  pending.delete(message.id);
  clearTimeout(call.timer);
  if (message.type === 'RESULT') call.resolve(message.payload);
  else
    call.reject(
      new Error(
        (message.payload as { message?: string } | undefined)?.message ??
          'Erreur inconnue de la tablette.',
      ),
    );
};

// Une commande à la tablette ; l'erreur rejetée porte un message écrit pour le caissier.
export const request = <T>(
  type: string,
  payload?: unknown,
  timeoutMs = 10_000,
): Promise<T> => {
  const bridge = window.ReactNativeWebView;
  if (!bridge)
    return Promise.reject(
      new Error('Fonction disponible uniquement sur la tablette.'),
    );
  if (!listening) {
    window.addEventListener('message', onMessage);
    listening = true;
  }
  return new Promise<T>((resolve, reject) => {
    const id = uuidv4();
    const timer = setTimeout(() => {
      pending.delete(id);
      reject(new Error('La tablette n’a pas répondu à temps.'));
    }, timeoutMs);
    pending.set(id, { resolve: resolve as Pending['resolve'], reject, timer });
    bridge.postMessage(JSON.stringify({ id, type, payload }));
  });
};
